import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Check, Signature } from '@/components/icons'
import { Button } from '../ui/button'
import { SignaturePad } from '../SignaturePad'
import {
  PrntrGroup,
  PrntrInput,
  PrntrValue,
  SectionLabel,
} from '../ui/prntr-input'
import type { FlightLogSignOffPhase } from '@@api/flightLog.shared'

// One captain sign-off phase (PRE FLIGHT / POST FLIGHT): PIC name mirrored
// from the crew, licence read-only from the PIC's crew record, signature
// drawn on the pad.
export function SignOffBlock({
  label,
  phase,
  readOnly,
  onSign,
  onClear,
}: {
  label: string
  phase: FlightLogSignOffPhase
  readOnly?: boolean
  onSign: (dataUrl: string) => void
  onClear: () => void
}) {
  const [signing, setSigning] = useState(false)
  const signed = !!phase.signaturePng

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{label}</SectionLabel>
      <PrntrGroup>
        <PrntrInput label="PIC">
          <PrntrValue value={phase.name} tone="info" placeholder="—" />
        </PrntrInput>
        <PrntrInput label="Licence No">
          <PrntrValue value={phase.licenceNo} tone="info" placeholder="—" />
        </PrntrInput>
      </PrntrGroup>

      {signed ? (
        <div className="flex flex-col gap-2 rounded-2xl bg-background-positive-subtle p-3">
          <img
            src={phase.signaturePng ?? undefined}
            alt={`${phase.name} signature`}
            className="mx-auto h-12 object-contain"
          />
          <p className="flex items-center justify-center gap-1.5 text-center text-sm font-semibold text-content-positive-bold">
            <Check className="size-4" />
            Signed {phase.signedAt ? dayjs(phase.signedAt).format('D MMM, HH:mm') : ''} UTC
          </p>
          {!readOnly && (
            <Button type="button" variant="secondary" size="mini" onClick={onClear}>
              Re-sign
            </Button>
          )}
        </div>
      ) : signing ? (
        <SignaturePad
          onCancel={() => setSigning(false)}
          onDone={(dataUrl) => {
            setSigning(false)
            onSign(dataUrl)
          }}
        />
      ) : (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => {
            if (!phase.licenceNo.trim()) {
              toast.error("No licence number on this pilot's crew profile")
              return
            }
            setSigning(true)
          }}
          className="flex h-[103px] flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-border-L3 text-sm font-semibold text-content-primary-subtle disabled:opacity-50"
        >
          <Signature className="size-6" />
          Tap to Sign
        </button>
      )}
    </div>
  )
}
